// The ONE catalog every server refusal is rendered through (SM-712).
//
// broker-service answers every refusal with a stable code; this catalog
// turns the ones staff meet into a sentence and, where there is one, what
// to do next, in both shipped locales. A code the catalog does not know
// is shown with the SERVER's own message — never a generic "something
// went wrong" that hides what the authority said — and the raw code is
// always printed beside it, so support can search for it.
//
// Money-command copy never tells the user to "try again": a pool transfer
// that failed for an operational reason is retried only through its own
// dialog, which replays the same Idempotency-Key (lib/idempotency.ts).
//
// A code can carry copy per details.rule (SM-835: the fee and share
// refusals name the rule they broke). A rule the catalog does not know
// keeps the code's copy, and the raw rule is printed beside the code, as
// an unknown code's is. A code whose refusal names its variant under
// another key says so (ruleDetail: the collateral guard's details.reason,
// SM-941). A hint can quote the refusal's details (the redemption ceiling
// names both rates and the highest allowed one).
//
// Pure module (no React): node --test covers it.
import { interpolate, type Locale } from "./core.ts";
import { formatRate } from "../lib/units.ts";

export interface ErrorCopy {
  title: string;
  hint?: string;
}

/** One code's copy, optionally per details.rule. */
interface CatalogEntry extends ErrorCopy {
  /** Copy for a details.rule; a rule not listed keeps the code's copy and is shown raw. */
  rules?: Record<string, ErrorCopy>;
  /** The details key whose value picks `rules` (default "rule"): the collateral guard names its reason (SM-941). */
  ruleDetail?: string;
  /** The hint used when DETAIL_PARAMS can read the refusal's details ({placeholders}). */
  detailHint?: string;
}

type Catalog = Record<string, CatalogEntry>;

const EN: Catalog = {
  // --- the node's GOLD currency image (SM-978) ---
  IMAGE_TOO_LARGE: {
    title: "This image is too large.",
    hint: "Choose an image of 10 MB or less — a picture saved for printing is too big to show next to an amount.",
  },
  IMAGE_TYPE_NOT_ALLOWED: {
    title: "This is not an image we accept.",
    hint: "Choose a PNG, JPEG, WebP or SVG file.",
  },
  IMAGE_UNREADABLE: {
    title: "We couldn't read this file.",
    hint: "Save the image again from your image editor, or choose another one, and try again.",
  },
  SYSTEM_BROKER_NOT_ALLOWED: {
    title: "The SYSTEM_BROKER's currency image cannot change.",
    hint: "Its users always see the platform's Gold coin.",
  },
  // --- lifecycle (contracts broker/v1 codes.go) ---
  BROKER_PAUSED: {
    title: "This broker is paused.",
    hint: "New exposure is stopped while a node on its path is paused. Risk-reducing actions still work. The exchange resumes it.",
  },
  BROKER_CLOSED: {
    title: "This broker is closed.",
    hint: "A closed node's records stay readable, but nothing can change on it.",
  },
  BROKER_NOT_APPROVED: {
    title: "This broker has not been approved yet.",
    hint: "A draft node waits for the exchange's approval before it can take users, funding or orders.",
  },
  BROKER_LIFECYCLE_CONFLICT: {
    title: "This record changed while you were looking at it.",
    hint: "Reload the page to see the current state before acting again.",
  },
  BROKER_HIERARCHY_INVALID: {
    title: "That change is not valid in this hierarchy.",
  },
  // a sub-broker's trading modes must be a subset of its parent's (SM-863, 422)
  BROKER_TRADING_MODES_EXCEED_PARENT: {
    title: "A sub-broker cannot trade a mode its parent does not.",
    hint: "A sub-broker's trading modes stay within its parent's, and a parent keeps every mode a sub-broker still uses. The exchange sets trading modes; nothing was saved.",
  },
  // only the SYSTEM_BROKER trades in game mode; a broker node is CASH only (SM-866, 422)
  BROKER_TRADING_MODE_NOT_ALLOWED: {
    title: "A broker trades in cash mode only.",
    hint: "Only the exchange's system broker trades in game mode, so every other broker trades GOLD only. The exchange sets trading modes; nothing was saved.",
  },
  // inbound money in a currency the node does not trade (SM-866, 422)
  BROKER_CURRENCY_NOT_TRADED: {
    title: "This node does not trade that currency.",
    hint: "A broker node holds and receives money only in the currencies it trades, and every broker trades GOLD only. Nothing was moved.",
    detailHint: "This node trades {currencies} only, so it cannot receive {currency}. Nothing was moved.",
  },
  // --- scope and authorization ---
  BROKER_SCOPE_DENIED: {
    title: "You do not have broker access here.",
    hint: "Your account holds no active membership that covers this node.",
  },
  BROKER_000103: {
    title: "Your role cannot do this on this node.",
    hint: "Ask an owner or administrator of this node if you need the access.",
  },
  BROKER_000102: {
    title: "Your session has ended.",
    hint: "Sign in again to continue.",
  },
  BROKER_000106: {
    title: "Not found in your scope.",
    hint: "It does not exist, or it sits outside the nodes your memberships cover.",
  },
  BROKER_000101: {
    title: "The request was not accepted.",
  },
  BROKER_000100: {
    title: "broker-service could not complete the request.",
  },
  BROKER_000104: {
    title: "A service this screen depends on is unavailable.",
    hint: "The figures are not shown rather than shown wrong. Reload in a moment.",
  },
  BROKER_000105: {
    title: "broker-service could not read or write its records.",
  },
  BROKER_STATE_UNAVAILABLE: {
    title: "The authoritative source did not answer.",
    hint: "Nothing is shown rather than a stale or partial figure. Reload in a moment.",
  },
  // a subtree read over 500 nodes (422)
  BROKER_000111: {
    title: "This subtree is too large for one read.",
    hint: "Switch Scope to Serviced directly, or narrow to a descendant node.",
  },
  // --- staff (SM-682) ---
  BROKER_STAFF_LAST_OWNER: {
    title: "A node must keep at least one active owner.",
    hint: "Make another member an owner first, then change or remove this one.",
  },
  BROKER_STAFF_ESCALATION: {
    title: "You cannot grant a role above your own.",
    hint: "Invite or re-role only up to the role you hold on this node.",
  },
  BROKER_000108: {
    title: "That user already has a membership on this node.",
  },
  // --- funding (SM-692 / SM-696) ---
  BROKER_FUNDING_EDGE_INVALID: {
    title: "Funds can only move across one direct edge.",
    hint: "Allocate to a direct child, or return to the immediate parent.",
  },
  BROKER_FUNDING_INSUFFICIENT: {
    title: "The source pool does not have enough available balance.",
    hint: "Nothing was moved.",
  },
  // a pool return the exposure collateral guard refuses (SM-768 §6; 409), by details.reason (SM-941)
  BROKER_COLLATERAL_WITHDRAWAL_REFUSED: {
    title: "This movement would take out collateral the tree still needs.",
    hint: "Nothing was moved.",
    ruleDetail: "reason",
    rules: {
      RECEIVABLE_OPEN: {
        title: "Collateral cannot leave while the exchange has an open receivable on this tree.",
        hint: "Your root broker owes the exchange a receivable that is not resolved yet. A pool return that takes collateral out is refused until the exchange resolves it. Nothing was moved.",
      },
      HEADROOM: {
        title: "This return would leave a limited node over its exposure limit.",
        hint: "Return a smaller amount, or wait for open positions to close and free headroom. Nothing was moved.",
      },
    },
  },
  BROKER_FUNDING_POOL_MISSING: {
    title: "One of the pools does not exist in this currency.",
    hint: "The node's trading modes decide which currencies it holds. Nothing was moved.",
  },
  BROKER_ALLOCATION_INELIGIBLE: {
    title: "That destination cannot receive funds.",
    hint: "Nothing was moved.",
  },
  // the money boundary's own refusals, relayed as a transfer's failure_code (broker-service refusalCode, SM-957)
  NATIVE_FUNDING_DISABLED: {
    title: "Funding by the exchange is turned off for this account.",
    hint: "Nothing was moved.",
  },
  EXTERNAL_CREDIT_MODE_UNSUPPORTED: {
    title: "This is not available for an external-credit account.",
    hint: "An external-credit account is funded by its broker's own provider. Nothing was moved.",
  },
  BROKER_000109: {
    title: "The ledger refused the transfer.",
    hint: "Nothing was moved. The transfer's record shows the ledger's reason.",
  },
  BROKER_IDEMPOTENCY_KEY_REQUIRED: {
    title: "The transfer was sent without its request key.",
    hint: "Close the dialog and open it again; nothing was moved.",
  },
  BROKER_IDEMPOTENCY_CONFLICT: {
    title: "This request key was already used for a different transfer.",
    hint: "Nothing new was moved. Close the dialog and check the transfers list before starting a new transfer.",
  },
  // --- fees, exposure, clearing ---
  BROKER_FEE_ASSIGNMENT_MISSING: {
    title: "No fee plan applies to this node in this currency.",
    hint: "The exchange or the root broker assigns fee plans. Orders from this node's users are refused until one applies.",
    rules: {
      NO_ASSIGNMENT: {
        title: "No fee plan is assigned on this node's path.",
        hint: "No NODE, BRANCH or GROUP rule applies here, or every rule on the path was cleared. Orders and GOLD redemptions (manual and auto-redeem) from this node's users are refused until a plan is assigned.",
      },
      PLAN_RETIRED: {
        title: "The fee plan assigned on this node's path is retired.",
        hint: "Fee rules do not fall back to a looser rule: orders and GOLD redemptions (manual and auto-redeem, positions already held included) are refused until the scope is assigned another plan.",
      },
      NO_VERSION_IN_CURRENCY: {
        title: "The assigned fee plan has no version in this currency in effect.",
        hint: "Fee rules do not fall back: orders in this currency are refused until the plan has a version in it, or the scope is assigned another plan.",
      },
      PLAN_UNKNOWN: {
        title: "The assigned fee plan does not exist.",
        hint: "Orders are refused until the scope is assigned an existing plan.",
      },
      PLAN_OWNER_MISMATCH: {
        title: "The assigned fee plan belongs to another root.",
        hint: "Orders are refused until the scope is assigned one of this root's plans.",
      },
    },
  },
  BROKER_FEE_ASSIGNMENT_CONFLICT: {
    title: "An assignment for this scope and target at this time already exists.",
    hint: "Two rules for the same scope, target and effective time would be ambiguous, so nothing was saved. Choose another effective time.",
  },
  // --- fee-plan authoring (SM-835: the root's owners and finance staff, or the exchange) ---
  // a version in a currency no node of the tree trades (SM-851, 400)
  BROKER_FEE_CURRENCY_NOT_TRADED: {
    title: "No node in this tree trades that currency.",
    hint: "A version in it could never apply to an order. Publish it in a currency the tree trades.",
  },
  BROKER_FEE_OWNER_MISMATCH: {
    title: "That fee plan or target is not this root's.",
    hint: "A root broker owns and assigns fee plans inside its own tree only.",
    rules: {
      NOT_ROOT: {
        title: "Only a root broker owns and assigns fee plans.",
        hint: "A sub-broker reads the rates that apply to it and never authors them.",
      },
      PLAN_OF_ANOTHER_ROOT: {
        title: "That fee plan belongs to another root.",
        hint: "Choose one of this root's own plans.",
      },
      TARGET_OUTSIDE_TREE: {
        title: "That target is not in this root's tree.",
        hint: "Assign only to the root or a node below it.",
      },
      GROUP_TARGET_NOT_ROOT: {
        title: "A GROUP rule targets the root itself.",
        hint: "Use BRANCH or NODE to target a node below the root.",
      },
    },
  },
  BROKER_FEE_PLAN_IMMUTABLE: {
    title: "This fee plan cannot be changed.",
    hint: "Published versions are never edited: a change is a new version with its own effective time.",
    rules: {
      SYSTEM_BROKER: {
        title: "The system broker's fee plan is protected.",
        hint: "Users without a broker pay exactly the exchange's fee, so nothing can be published for them.",
      },
      PROTECTED_PLAN: {
        title: "This fee plan is protected.",
        hint: "A protected plan takes no new version and cannot be retired.",
      },
      PLAN_RETIRED: {
        title: "The fee plan is retired at that time.",
        hint: "A retired plan takes no new version and no new assignment. Create a new plan instead.",
      },
      ALREADY_RETIRED: {
        title: "The fee plan is already retired.",
        hint: "A plan retires once, and its retirement time cannot be changed.",
      },
      PLAN_RETIRING: {
        title: "The fee plan has a retirement scheduled.",
        hint: "A rule naming it would refuse every order and GOLD redemption under the scope from its retire time. Assign another plan.",
      },
    },
  },
  // retiring a plan a rule still names (SM-864, 409)
  BROKER_FEE_PLAN_IN_USE: {
    title: "This fee plan is still assigned.",
    hint: "Retiring it would refuse every order and every GOLD redemption (manual and auto-redeem) under the scopes that name it. Reassign or clear those scopes from the retire time or earlier, then retire the plan.",
  },
  BROKER_FEE_RATE_INVALID: {
    title: "A rate is not valid.",
    hint: "Each rate is a percentage from 0 to 100 with at most 4 decimals. Nothing was published.",
  },
  BROKER_FEE_CEILING_EXCEEDED: {
    title: "The broker redemption rate is above the ceiling.",
    hint: "The GOLD broker redemption rate plus the exchange's highest redemption rate must stay within 100%. Nothing was published.",
    detailHint:
      "The exchange's highest redemption rate is {exchange} and this broker rate is {broker}: together they exceed 100%. The highest broker redemption rate allowed is {allowed}. Nothing was published.",
  },
  // --- revenue-share splits (SM-845: a fee schedule read on a broken split; SM-846: authoring) ---
  BROKER_REVENUE_SHARE_INVALID: {
    title: "The revenue-share split is not valid.",
    hint: "The root's owners or finance staff fix it with a corrected version or another assignment.",
    rules: {
      NO_SHARES: {
        title: "A version needs at least one share.",
        hint: "Add a recipient with a share. Nothing was saved.",
      },
      SHARE_NOT_POSITIVE: {
        title: "Each share is 1 to 10,000 basis points.",
        hint: "Nothing was saved.",
      },
      SHARES_NOT_10000: {
        title: "The shares must total exactly 10,000 basis points (100%).",
        hint: "Nothing was saved.",
      },
      RECIPIENT_REPEATED: {
        title: "A recipient appears more than once.",
        hint: "Name each recipient once. Nothing was saved.",
      },
      RECIPIENT_INVALID: {
        title: "A recipient is not valid.",
        hint: "A recipient is a broker id of this tree, or @servicing. Nothing was saved.",
      },
      REMAINDER_NOT_RECIPIENT: {
        title: "The remainder recipient must hold a share.",
        hint: "Choose one of the version's recipients. Nothing was saved.",
      },
      RECIPIENT_OUTSIDE_TREE: {
        title: "A recipient is not in this root's tree.",
        hint: "Recipients are nodes of this tree, or the servicing broker. Nothing was saved.",
      },
      RECIPIENT_CLOSED: {
        title: "A closed broker cannot receive a share.",
        hint: "Remove it from the split. Nothing was saved.",
      },
      TARGET_OUTSIDE_TREE: {
        title: "That target is not in this root's tree.",
        hint: "Assign only to the root or a node below it.",
      },
      GROUP_TARGET_NOT_ROOT: {
        title: "A GROUP rule targets the root itself.",
        hint: "Use BRANCH or NODE to target a node below the root.",
      },
      OFF_PATH: {
        title: "A revenue-share recipient is off the path.",
        hint: "Every recipient a split names must be the assignment's target or a node above it, so that it is on the path of each order the split applies to. Orders that resolve to such a split are refused; the root's owners or finance staff fix it with a corrected version or another assignment.",
      },
      SERVICING_NAMED_TWICE: {
        title: "The split names the servicing broker twice.",
        hint: "A version with a servicing-broker (@servicing) share cannot also name its assignment target: on the target's own orders both mean the same broker. Orders that resolve to such a split are refused; the root's owners or finance staff fix it with a corrected version or another assignment.",
      },
    },
  },
  BROKER_REVENUE_SHARE_CONFLICT: {
    title: "That change conflicts with the revenue-share plan's state.",
    hint: "Nothing was saved.",
    rules: {
      PLAN_RETIRED: {
        title: "The revenue-share plan is retired at that time.",
        hint: "A retired plan takes no new version or assignment. Nothing was saved.",
      },
      ALREADY_RETIRED: {
        title: "The revenue-share plan is already retired.",
        hint: "A plan retires once. Nothing was saved.",
      },
      DUPLICATE_EFFECTIVE_AT: {
        title: "A share assignment for this scope and target at this time already exists.",
        hint: "Two rules for the same scope, target and effective time would be ambiguous, so nothing was saved. Choose another effective time.",
      },
    },
  },
  BROKER_EXPOSURE_UNAVAILABLE: {
    title: "Exposure could not be read right now.",
    hint: "Collateral is read live from the ledger; the figure is not shown rather than shown stale.",
  },
  BROKER_CLEARING_UNAVAILABLE: {
    title: "The clearing balance could not be read right now.",
  },
  // --- transport ---
  NETWORK_ERROR: {
    title: "The portal could not reach the server.",
    hint: "Check your connection. A money command is never re-sent automatically.",
  },
};

const TH: Catalog = {
  // --- the node's GOLD currency image (SM-978) ---
  IMAGE_TOO_LARGE: {
    title: "รูปนี้ใหญ่เกินไป",
    hint: "เลือกรูปที่มีขนาดไม่เกิน 10 MB รูปที่บันทึกไว้สำหรับงานพิมพ์ใหญ่เกินกว่าจะแสดงข้างยอดเงิน",
  },
  IMAGE_TYPE_NOT_ALLOWED: {
    title: "ไฟล์นี้ไม่ใช่รูปที่รองรับ",
    hint: "เลือกไฟล์ PNG, JPEG, WebP หรือ SVG",
  },
  IMAGE_UNREADABLE: {
    title: "อ่านไฟล์นี้ไม่ได้",
    hint: "บันทึกรูปใหม่จากโปรแกรมแต่งรูป หรือเลือกรูปอื่น แล้วลองอีกครั้ง",
  },
  SYSTEM_BROKER_NOT_ALLOWED: {
    title: "เปลี่ยนรูปสกุลเงินของ SYSTEM_BROKER ไม่ได้",
    hint: "ผู้ใช้ของ SYSTEM_BROKER เห็นเหรียญ Gold ของแพลตฟอร์มเสมอ",
  },
  BROKER_PAUSED: {
    title: "โบรกเกอร์นี้ถูกพักการทำงาน",
    hint: "ระบบหยุดการสร้างความเสี่ยงใหม่ระหว่างที่โหนดในสายนี้ถูกพัก การลดความเสี่ยงยังทำได้ และตลาดเป็นผู้เปิดใช้งานอีกครั้ง",
  },
  BROKER_CLOSED: {
    title: "โบรกเกอร์นี้ถูกปิดแล้ว",
    hint: "ยังดูข้อมูลของโหนดที่ปิดแล้วได้ แต่ไม่สามารถเปลี่ยนแปลงสิ่งใดได้",
  },
  BROKER_NOT_APPROVED: {
    title: "โบรกเกอร์นี้ยังไม่ได้รับการอนุมัติ",
    hint: "โหนดฉบับร่างต้องรอตลาดอนุมัติก่อนจึงจะรับผู้ใช้ เงินทุน หรือคำสั่งซื้อขายได้",
  },
  BROKER_LIFECYCLE_CONFLICT: {
    title: "ข้อมูลนี้เปลี่ยนไประหว่างที่คุณกำลังดูอยู่",
    hint: "โหลดหน้าใหม่เพื่อดูสถานะปัจจุบันก่อนดำเนินการอีกครั้ง",
  },
  BROKER_HIERARCHY_INVALID: {
    title: "การเปลี่ยนแปลงนี้ไม่ถูกต้องในลำดับชั้นนี้",
  },
  BROKER_TRADING_MODES_EXCEED_PARENT: {
    title: "โบรกเกอร์ย่อยซื้อขายในโหมดที่โบรกเกอร์แม่ไม่มีไม่ได้",
    hint: "โหมดการซื้อขายของโบรกเกอร์ย่อยต้องอยู่ภายในโหมดของโบรกเกอร์แม่ และโบรกเกอร์แม่ต้องคงทุกโหมดที่โบรกเกอร์ย่อยยังใช้อยู่ ตลาดเป็นผู้กำหนดโหมดการซื้อขาย ไม่มีการบันทึกใด ๆ",
  },
  BROKER_TRADING_MODE_NOT_ALLOWED: {
    title: "โบรกเกอร์ซื้อขายได้เฉพาะโหมดเงินสดเท่านั้น",
    hint: "มีเพียงโบรกเกอร์ระบบของตลาดที่ซื้อขายในโหมดเกมได้ โบรกเกอร์อื่นทั้งหมดจึงซื้อขายด้วย GOLD เท่านั้น ตลาดเป็นผู้กำหนดโหมดการซื้อขาย ไม่มีการบันทึกใด ๆ",
  },
  BROKER_CURRENCY_NOT_TRADED: {
    title: "โหนดนี้ไม่ได้ซื้อขายด้วยสกุลเงินนั้น",
    hint: "โหนดโบรกเกอร์ถือและรับเงินได้เฉพาะในสกุลเงินที่ตนซื้อขาย และโบรกเกอร์ทุกรายซื้อขายด้วย GOLD เท่านั้น ไม่มีการโอนเงินใด ๆ",
    detailHint: "โหนดนี้ซื้อขายด้วย {currencies} เท่านั้น จึงรับเงินสกุล {currency} ไม่ได้ ไม่มีการโอนเงินใด ๆ",
  },
  BROKER_SCOPE_DENIED: {
    title: "คุณไม่มีสิทธิ์เข้าถึงโบรกเกอร์ในส่วนนี้",
    hint: "บัญชีของคุณไม่มีสมาชิกภาพที่ใช้งานอยู่ซึ่งครอบคลุมโหนดนี้",
  },
  BROKER_000103: {
    title: "บทบาทของคุณไม่สามารถทำรายการนี้บนโหนดนี้ได้",
    hint: "หากต้องการสิทธิ์ โปรดติดต่อเจ้าของหรือผู้ดูแลของโหนดนี้",
  },
  BROKER_000102: {
    title: "เซสชันของคุณสิ้นสุดแล้ว",
    hint: "โปรดเข้าสู่ระบบอีกครั้งเพื่อดำเนินการต่อ",
  },
  BROKER_000106: {
    title: "ไม่พบในขอบเขตของคุณ",
    hint: "รายการนี้ไม่มีอยู่ หรืออยู่นอกโหนดที่สมาชิกภาพของคุณครอบคลุม",
  },
  BROKER_000101: {
    title: "คำขอไม่ได้รับการยอมรับ",
  },
  BROKER_000100: {
    title: "บริการโบรกเกอร์ไม่สามารถดำเนินการคำขอได้",
  },
  BROKER_000104: {
    title: "บริการที่หน้านี้ต้องใช้ไม่พร้อมใช้งาน",
    hint: "ระบบไม่แสดงตัวเลขแทนการแสดงตัวเลขที่ผิด โปรดโหลดใหม่ในอีกสักครู่",
  },
  BROKER_000105: {
    title: "บริการโบรกเกอร์ไม่สามารถอ่านหรือบันทึกข้อมูลได้",
  },
  BROKER_STATE_UNAVAILABLE: {
    title: "แหล่งข้อมูลหลักไม่ตอบสนอง",
    hint: "ระบบไม่แสดงตัวเลขที่ค้างหรือไม่ครบถ้วน โปรดโหลดใหม่ในอีกสักครู่",
  },
  BROKER_000111: {
    title: "ลำดับชั้นย่อยนี้ใหญ่เกินกว่าจะอ่านได้ในครั้งเดียว",
    hint: "เปลี่ยนขอบเขตเป็น “ให้บริการโดยตรง” หรือเลือกโหนดลูกหลานเพื่อให้ขอบเขตแคบลง",
  },
  BROKER_STAFF_LAST_OWNER: {
    title: "โหนดต้องมีเจ้าของที่ใช้งานอยู่อย่างน้อยหนึ่งคน",
    hint: "แต่งตั้งสมาชิกคนอื่นเป็นเจ้าของก่อน แล้วจึงเปลี่ยนหรือนำคนนี้ออก",
  },
  BROKER_STAFF_ESCALATION: {
    title: "คุณไม่สามารถให้บทบาทที่สูงกว่าบทบาทของคุณเองได้",
    hint: "เชิญหรือเปลี่ยนบทบาทได้ไม่เกินบทบาทที่คุณมีบนโหนดนี้",
  },
  BROKER_000108: {
    title: "ผู้ใช้นี้มีสมาชิกภาพบนโหนดนี้อยู่แล้ว",
  },
  BROKER_FUNDING_EDGE_INVALID: {
    title: "โอนเงินได้เฉพาะระหว่างโหนดที่เชื่อมกันโดยตรงเท่านั้น",
    hint: "จัดสรรให้โหนดลูกโดยตรง หรือคืนให้โหนดแม่ที่อยู่ถัดขึ้นไป",
  },
  BROKER_FUNDING_INSUFFICIENT: {
    title: "พูลต้นทางมียอดคงเหลือที่ใช้ได้ไม่เพียงพอ",
    hint: "ไม่มีการโอนเงินใด ๆ",
  },
  BROKER_COLLATERAL_WITHDRAWAL_REFUSED: {
    title: "การโอนนี้จะดึงหลักประกันที่ลำดับชั้นยังต้องใช้ออกไป",
    hint: "ไม่มีการโอนเงินใด ๆ",
    ruleDetail: "reason",
    rules: {
      RECEIVABLE_OPEN: {
        title: "ยังนำหลักประกันออกไม่ได้ ขณะที่ตลาดมีลูกหนี้ค้างอยู่ในลำดับชั้นนี้",
        hint: "โบรกเกอร์รากของคุณมียอดลูกหนี้กับตลาดที่ยังไม่ได้รับการแก้ไข การคืนเงินจากพูลที่นำหลักประกันออกจะถูกปฏิเสธจนกว่าตลาดจะแก้ไขรายการนั้น ไม่มีการโอนเงินใด ๆ",
      },
      HEADROOM: {
        title: "การคืนเงินนี้จะทำให้โหนดที่มีวงเงินเกินวงเงินความเสี่ยง",
        hint: "คืนเงินจำนวนน้อยลง หรือรอให้สถานะที่เปิดอยู่ปิดลงเพื่อให้มีวงเงินคงเหลือเพิ่มขึ้น ไม่มีการโอนเงินใด ๆ",
      },
    },
  },
  BROKER_FUNDING_POOL_MISSING: {
    title: "พูลฝั่งใดฝั่งหนึ่งไม่มีในสกุลเงินนี้",
    hint: "โหมดการซื้อขายของโหนดเป็นตัวกำหนดสกุลเงินที่โหนดถือ ไม่มีการโอนเงินใด ๆ",
  },
  BROKER_ALLOCATION_INELIGIBLE: {
    title: "ปลายทางนี้ไม่สามารถรับเงินได้",
    hint: "ไม่มีการโอนเงินใด ๆ",
  },
  NATIVE_FUNDING_DISABLED: {
    title: "บัญชีนี้ปิดการจัดหาเงินโดยตลาดอยู่",
    hint: "ไม่มีการโอนเงินใด ๆ",
  },
  EXTERNAL_CREDIT_MODE_UNSUPPORTED: {
    title: "บัญชีเครดิตภายนอกใช้รายการนี้ไม่ได้",
    hint: "บัญชีเครดิตภายนอกได้รับเงินจากผู้ให้บริการของโบรกเกอร์เอง ไม่มีการโอนเงินใด ๆ",
  },
  BROKER_000109: {
    title: "บัญชีแยกประเภทปฏิเสธการโอน",
    hint: "ไม่มีการโอนเงินใด ๆ รายการโอนจะแสดงเหตุผลจากบัญชีแยกประเภท",
  },
  BROKER_IDEMPOTENCY_KEY_REQUIRED: {
    title: "คำขอโอนถูกส่งโดยไม่มีรหัสคำขอ",
    hint: "ปิดหน้าต่างแล้วเปิดใหม่ ไม่มีการโอนเงินใด ๆ",
  },
  BROKER_IDEMPOTENCY_CONFLICT: {
    title: "รหัสคำขอนี้ถูกใช้กับการโอนอื่นไปแล้ว",
    hint: "ไม่มีการโอนเงินเพิ่ม ปิดหน้าต่างและตรวจสอบรายการโอนก่อนเริ่มการโอนใหม่",
  },
  BROKER_FEE_ASSIGNMENT_MISSING: {
    title: "ไม่มีแผนค่าธรรมเนียมที่ใช้กับโหนดนี้ในสกุลเงินนี้",
    hint: "ตลาดหรือโบรกเกอร์รากเป็นผู้กำหนดแผนค่าธรรมเนียม คำสั่งซื้อขายของผู้ใช้ในโหนดนี้จะถูกปฏิเสธจนกว่าจะมีแผนที่ใช้ได้",
    rules: {
      NO_ASSIGNMENT: {
        title: "ไม่มีการกำหนดแผนค่าธรรมเนียมบนเส้นทางของโหนดนี้",
        hint: "ไม่มีกฎระดับ NODE, BRANCH หรือ GROUP ที่ใช้กับที่นี่ หรือกฎทั้งหมดบนเส้นทางถูกยกเลิกแล้ว คำสั่งซื้อขายและการแลกรับ GOLD (ทั้งแบบทำเองและ auto-redeem) ของผู้ใช้ในโหนดนี้จะถูกปฏิเสธจนกว่าจะมีการกำหนดแผน",
      },
      PLAN_RETIRED: {
        title: "แผนค่าธรรมเนียมที่กำหนดบนเส้นทางของโหนดนี้เลิกใช้แล้ว",
        hint: "กฎค่าธรรมเนียมไม่ถอยไปใช้กฎที่กว้างกว่า คำสั่งซื้อขายและการแลกรับ GOLD (ทั้งแบบทำเองและ auto-redeem รวมถึงสถานะที่ถืออยู่แล้ว) จะถูกปฏิเสธจนกว่าขอบเขตนั้นจะได้รับการกำหนดแผนอื่น",
      },
      NO_VERSION_IN_CURRENCY: {
        title: "แผนค่าธรรมเนียมที่กำหนดไม่มีเวอร์ชันในสกุลเงินนี้ที่มีผลอยู่",
        hint: "กฎค่าธรรมเนียมไม่ถอยไปใช้กฎถัดไป คำสั่งซื้อขายในสกุลเงินนี้จะถูกปฏิเสธจนกว่าแผนจะมีเวอร์ชันในสกุลนี้ หรือขอบเขตนั้นได้รับการกำหนดแผนอื่น",
      },
      PLAN_UNKNOWN: {
        title: "ไม่พบแผนค่าธรรมเนียมที่กำหนดไว้",
        hint: "คำสั่งซื้อขายจะถูกปฏิเสธจนกว่าขอบเขตนั้นจะได้รับการกำหนดแผนที่มีอยู่จริง",
      },
      PLAN_OWNER_MISMATCH: {
        title: "แผนค่าธรรมเนียมที่กำหนดเป็นของโบรกเกอร์รากอื่น",
        hint: "คำสั่งซื้อขายจะถูกปฏิเสธจนกว่าขอบเขตนั้นจะได้รับการกำหนดแผนของโบรกเกอร์รากนี้",
      },
    },
  },
  BROKER_FEE_ASSIGNMENT_CONFLICT: {
    title: "มีการกำหนดสำหรับขอบเขตและเป้าหมายนี้ ณ เวลานี้อยู่แล้ว",
    hint: "กฎสองรายการที่มีขอบเขต เป้าหมาย และเวลาเริ่มมีผลเดียวกันจะกำกวม จึงไม่มีการบันทึกใด ๆ โปรดเลือกเวลาเริ่มมีผลอื่น",
  },
  BROKER_FEE_CURRENCY_NOT_TRADED: {
    title: "ไม่มีโหนดใดในลำดับชั้นนี้ซื้อขายด้วยสกุลเงินนั้น",
    hint: "เวอร์ชันในสกุลเงินนี้จะไม่มีวันใช้กับคำสั่งใดได้ โปรดเผยแพร่ในสกุลเงินที่ลำดับชั้นนี้ซื้อขาย",
  },
  BROKER_FEE_OWNER_MISMATCH: {
    title: "แผนค่าธรรมเนียมหรือเป้าหมายนี้ไม่ได้เป็นของโบรกเกอร์รากนี้",
    hint: "โบรกเกอร์รากเป็นเจ้าของและกำหนดแผนค่าธรรมเนียมได้เฉพาะภายในลำดับชั้นของตนเองเท่านั้น",
    rules: {
      NOT_ROOT: {
        title: "เฉพาะโบรกเกอร์รากเท่านั้นที่เป็นเจ้าของและกำหนดแผนค่าธรรมเนียมได้",
        hint: "โบรกเกอร์ย่อยดูอัตราที่ใช้กับตนเองได้ แต่สร้างหรือแก้ไขแผนไม่ได้",
      },
      PLAN_OF_ANOTHER_ROOT: {
        title: "แผนค่าธรรมเนียมนี้เป็นของโบรกเกอร์รากอื่น",
        hint: "โปรดเลือกแผนของโบรกเกอร์รากนี้เอง",
      },
      TARGET_OUTSIDE_TREE: {
        title: "เป้าหมายนี้ไม่อยู่ในลำดับชั้นของโบรกเกอร์รากนี้",
        hint: "กำหนดได้เฉพาะโหนดรากหรือโหนดที่อยู่ใต้โหนดรากเท่านั้น",
      },
      GROUP_TARGET_NOT_ROOT: {
        title: "กฎระดับ GROUP ต้องมีเป้าหมายเป็นโหนดรากเอง",
        hint: "ใช้ BRANCH หรือ NODE เพื่อกำหนดเป้าหมายเป็นโหนดที่อยู่ใต้โหนดราก",
      },
    },
  },
  BROKER_FEE_PLAN_IMMUTABLE: {
    title: "แผนค่าธรรมเนียมนี้เปลี่ยนแปลงไม่ได้",
    hint: "เวอร์ชันที่เผยแพร่แล้วแก้ไขไม่ได้ การเปลี่ยนแปลงต้องทำเป็นเวอร์ชันใหม่ที่มีเวลาเริ่มมีผลของตัวเอง",
    rules: {
      SYSTEM_BROKER: {
        title: "แผนค่าธรรมเนียมของโบรกเกอร์ระบบได้รับการป้องกัน",
        hint: "ผู้ใช้ที่ไม่มีโบรกเกอร์เสียค่าธรรมเนียมเท่ากับของตลาดเท่านั้น จึงเผยแพร่สิ่งใดให้ไม่ได้",
      },
      PROTECTED_PLAN: {
        title: "แผนค่าธรรมเนียมนี้ได้รับการป้องกัน",
        hint: "แผนที่ได้รับการป้องกันเพิ่มเวอร์ชันใหม่หรือเลิกใช้ไม่ได้",
      },
      PLAN_RETIRED: {
        title: "แผนค่าธรรมเนียมนี้เลิกใช้แล้ว ณ เวลานั้น",
        hint: "แผนที่เลิกใช้แล้วรับเวอร์ชันใหม่หรือการกำหนดใหม่ไม่ได้ โปรดสร้างแผนใหม่แทน",
      },
      ALREADY_RETIRED: {
        title: "แผนค่าธรรมเนียมนี้เลิกใช้ไปแล้ว",
        hint: "แผนเลิกใช้ได้เพียงครั้งเดียว และเปลี่ยนเวลาเลิกใช้ภายหลังไม่ได้",
      },
      PLAN_RETIRING: {
        title: "แผนค่าธรรมเนียมนี้มีกำหนดเลิกใช้แล้ว",
        hint: "กฎที่ชี้มายังแผนนี้จะปฏิเสธคำสั่งและการแลกรับ GOLD ทุกรายการภายใต้ขอบเขตนั้นตั้งแต่เวลาเลิกใช้ โปรดกำหนดแผนอื่น",
      },
    },
  },
  BROKER_FEE_PLAN_IN_USE: {
    title: "แผนค่าธรรมเนียมนี้ยังถูกกำหนดใช้อยู่",
    hint: "การเลิกใช้แผนนี้จะทำให้คำสั่งและการแลกรับ GOLD ทุกรายการ (ทั้งแบบทำเองและ auto-redeem) ภายใต้ขอบเขตที่ชี้มายังแผนนี้ถูกปฏิเสธ โปรดกำหนดแผนใหม่หรือล้างกฎของขอบเขตเหล่านั้นให้มีผลตั้งแต่เวลาเลิกใช้หรือก่อนหน้านั้น แล้วจึงเลิกใช้แผน",
  },
  BROKER_FEE_RATE_INVALID: {
    title: "อัตราไม่ถูกต้อง",
    hint: "แต่ละอัตราเป็นเปอร์เซ็นต์ตั้งแต่ 0 ถึง 100 ทศนิยมไม่เกิน 4 ตำแหน่ง ไม่มีการเผยแพร่ใด ๆ",
  },
  BROKER_FEE_CEILING_EXCEEDED: {
    title: "อัตราการแลกรับของโบรกเกอร์สูงเกินเพดาน",
    hint: "อัตราการแลกรับ GOLD ของโบรกเกอร์รวมกับอัตราการแลกรับสูงสุดของตลาดต้องไม่เกิน 100% ไม่มีการเผยแพร่ใด ๆ",
    detailHint:
      "อัตราการแลกรับสูงสุดของตลาดคือ {exchange} และอัตราของโบรกเกอร์นี้คือ {broker} ซึ่งรวมกันเกิน 100% อัตราการแลกรับของโบรกเกอร์สูงสุดที่ทำได้คือ {allowed} ไม่มีการเผยแพร่ใด ๆ",
  },
  BROKER_REVENUE_SHARE_INVALID: {
    title: "การแบ่งรายได้นี้ไม่ถูกต้อง",
    hint: "เจ้าของหรือเจ้าหน้าที่การเงินของโบรกเกอร์รากแก้ไขได้ด้วยการเผยแพร่เวอร์ชันที่ถูกต้องหรือเปลี่ยนการกำหนด",
    rules: {
      NO_SHARES: {
        title: "เวอร์ชันต้องมีส่วนแบ่งอย่างน้อยหนึ่งรายการ",
        hint: "เพิ่มผู้รับพร้อมส่วนแบ่ง ไม่มีการบันทึกใด ๆ",
      },
      SHARE_NOT_POSITIVE: {
        title: "แต่ละส่วนแบ่งต้องอยู่ระหว่าง 1 ถึง 10,000 เบสิสพอยต์",
        hint: "ไม่มีการบันทึกใด ๆ",
      },
      SHARES_NOT_10000: {
        title: "ส่วนแบ่งรวมต้องเท่ากับ 10,000 เบสิสพอยต์ (100%) พอดี",
        hint: "ไม่มีการบันทึกใด ๆ",
      },
      RECIPIENT_REPEATED: {
        title: "มีผู้รับซ้ำกันมากกว่าหนึ่งครั้ง",
        hint: "ระบุผู้รับแต่ละรายเพียงครั้งเดียว ไม่มีการบันทึกใด ๆ",
      },
      RECIPIENT_INVALID: {
        title: "ผู้รับไม่ถูกต้อง",
        hint: "ผู้รับต้องเป็นรหัสโบรกเกอร์ในลำดับชั้นนี้ หรือ @servicing ไม่มีการบันทึกใด ๆ",
      },
      REMAINDER_NOT_RECIPIENT: {
        title: "ผู้รับเศษที่เหลือต้องมีส่วนแบ่ง",
        hint: "เลือกผู้รับรายใดรายหนึ่งในเวอร์ชันนี้ ไม่มีการบันทึกใด ๆ",
      },
      RECIPIENT_OUTSIDE_TREE: {
        title: "ผู้รับไม่อยู่ในลำดับชั้นของโบรกเกอร์รากนี้",
        hint: "ผู้รับต้องเป็นโหนดในลำดับชั้นนี้ หรือโบรกเกอร์ที่ให้บริการ ไม่มีการบันทึกใด ๆ",
      },
      RECIPIENT_CLOSED: {
        title: "โบรกเกอร์ที่ปิดแล้วรับส่วนแบ่งไม่ได้",
        hint: "นำโบรกเกอร์นั้นออกจากการแบ่ง ไม่มีการบันทึกใด ๆ",
      },
      TARGET_OUTSIDE_TREE: {
        title: "เป้าหมายนี้ไม่อยู่ในลำดับชั้นของโบรกเกอร์รากนี้",
        hint: "กำหนดได้เฉพาะโหนดรากหรือโหนดที่อยู่ใต้โหนดรากเท่านั้น",
      },
      GROUP_TARGET_NOT_ROOT: {
        title: "กฎระดับ GROUP ต้องมีเป้าหมายเป็นโหนดรากเอง",
        hint: "ใช้ BRANCH หรือ NODE เพื่อกำหนดเป้าหมายเป็นโหนดที่อยู่ใต้โหนดราก",
      },
      OFF_PATH: {
        title: "ผู้รับส่วนแบ่งรายได้อยู่นอกเส้นทาง",
        hint: "ผู้รับทุกรายที่การแบ่งระบุต้องเป็นเป้าหมายของการกำหนดหรือโหนดที่อยู่เหนือเป้าหมาย เพื่อให้อยู่บนเส้นทางของทุกคำสั่งที่ใช้การแบ่งนั้น คำสั่งที่ใช้การแบ่งเช่นนี้จะถูกปฏิเสธ เจ้าของหรือเจ้าหน้าที่การเงินของโบรกเกอร์รากแก้ไขได้ด้วยเวอร์ชันที่ถูกต้องหรือการกำหนดอื่น",
      },
      SERVICING_NAMED_TWICE: {
        title: "การแบ่งนี้ระบุโบรกเกอร์ที่ให้บริการซ้ำสองครั้ง",
        hint: "เวอร์ชันที่มีส่วนแบ่งของโบรกเกอร์ที่ให้บริการ (@servicing) จะระบุเป้าหมายของการกำหนดเพิ่มอีกไม่ได้ เพราะในคำสั่งของเป้าหมายเองทั้งสองหมายถึงโบรกเกอร์เดียวกัน คำสั่งที่ใช้การแบ่งเช่นนี้จะถูกปฏิเสธ เจ้าของหรือเจ้าหน้าที่การเงินของโบรกเกอร์รากแก้ไขได้ด้วยเวอร์ชันที่ถูกต้องหรือการกำหนดอื่น",
      },
    },
  },
  BROKER_REVENUE_SHARE_CONFLICT: {
    title: "การเปลี่ยนแปลงนี้ขัดกับสถานะของแผนแบ่งรายได้",
    hint: "ไม่มีการบันทึกใด ๆ",
    rules: {
      PLAN_RETIRED: {
        title: "แผนแบ่งรายได้นี้เลิกใช้แล้ว ณ เวลานั้น",
        hint: "แผนที่เลิกใช้แล้วรับเวอร์ชันหรือการกำหนดใหม่ไม่ได้ ไม่มีการบันทึกใด ๆ",
      },
      ALREADY_RETIRED: {
        title: "แผนแบ่งรายได้นี้เลิกใช้ไปแล้ว",
        hint: "แผนเลิกใช้ได้เพียงครั้งเดียว ไม่มีการบันทึกใด ๆ",
      },
      DUPLICATE_EFFECTIVE_AT: {
        title: "มีการกำหนดการแบ่งรายได้สำหรับขอบเขตและเป้าหมายนี้ ณ เวลานี้อยู่แล้ว",
        hint: "กฎสองรายการที่มีขอบเขต เป้าหมาย และเวลาเริ่มมีผลเดียวกันจะกำกวม จึงไม่มีการบันทึกใด ๆ โปรดเลือกเวลาเริ่มมีผลอื่น",
      },
    },
  },
  BROKER_EXPOSURE_UNAVAILABLE: {
    title: "ไม่สามารถอ่านความเสี่ยงได้ในขณะนี้",
    hint: "หลักประกันอ่านจากบัญชีแยกประเภทแบบเรียลไทม์ ระบบจึงไม่แสดงตัวเลขที่ค้าง",
  },
  BROKER_CLEARING_UNAVAILABLE: {
    title: "ไม่สามารถอ่านยอดชำระบัญชีได้ในขณะนี้",
  },
  NETWORK_ERROR: {
    title: "พอร์ทัลเชื่อมต่อเซิร์ฟเวอร์ไม่ได้",
    hint: "ตรวจสอบการเชื่อมต่อ ระบบจะไม่ส่งคำสั่งเกี่ยวกับเงินซ้ำโดยอัตโนมัติ",
  },
};

/** Every code the catalog covers (test/error-catalog.test.ts pins the list). */
export const CATALOG_CODES: readonly string[] = Object.keys(EN);

export function hasCatalogEntry(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(EN, code);
}

/** Status-based fallbacks for codes outside the catalog (gateway codes etc.). */
const STATUS_EN: Record<number, ErrorCopy> = {
  401: { title: "Your session has ended.", hint: "Sign in again to continue." },
  403: { title: "You are not allowed to do this." },
  404: { title: "Not found in your scope." },
  429: { title: "Too many requests right now.", hint: "Wait a moment before reloading." },
};
const STATUS_TH: Record<number, ErrorCopy> = {
  401: { title: "เซสชันของคุณสิ้นสุดแล้ว", hint: "โปรดเข้าสู่ระบบอีกครั้งเพื่อดำเนินการต่อ" },
  403: { title: "คุณไม่ได้รับอนุญาตให้ทำรายการนี้" },
  404: { title: "ไม่พบในขอบเขตของคุณ" },
  429: { title: "มีคำขอมากเกินไปในขณะนี้", hint: "โปรดรอสักครู่ก่อนโหลดใหม่" },
};

export interface ErrorLike {
  status?: number;
  code?: string;
  message?: string;
  /** The broker envelope's details (APIError.details); details.rule (or the entry's ruleDetail) picks the rule's copy. */
  details?: Record<string, string>;
}

export interface DescribedError {
  title: string;
  hint?: string;
  code?: string;
  /** details.rule (or the entry's ruleDetail), raw: printed beside the code whether or not the catalog knows it. */
  rule?: string;
  /** The server's own message, shown under the catalog copy when it adds something. */
  serverMessage?: string;
  fromCatalog: boolean;
}

function isGenericMessage(m: string | undefined): boolean {
  return !m || /^HTTP \d+$/.test(m);
}

// --- hints that quote the refusal's details ---------------------------------------

const DIGITS = /^\d+$/;

function precisionOf(v: string | undefined): number | null {
  const n = Number(v);
  return v !== undefined && DIGITS.test(v) && n >= 0 && n <= 18 ? n : null;
}

/**
 * BROKER_FEE_CEILING_EXCEEDED's figures as percentages: the exchange's
 * highest redemption rate, the refused broker rate, and the highest broker
 * rate allowed — max_combined_fee_rate (at fee_rate_precision) minus the
 * exchange's, computed exactly at the finer of the two precisions, never
 * below 0. null when a detail is missing or malformed.
 */
export function redemptionCeilingFigures(
  d: Record<string, string> | undefined,
): { exchange: string; broker: string; allowed: string } | null {
  if (!d) return null;
  const exchange = d.exchange_max_redemption_fee_rate;
  const broker = d.broker_redemption_fee_rate;
  const max = d.max_combined_fee_rate ?? "1000000";
  const exP = precisionOf(d.exchange_fee_rate_precision);
  const brP = precisionOf(d.fee_rate_precision ?? "6");
  if (!exchange || !broker || !DIGITS.test(exchange) || !DIGITS.test(broker) || !DIGITS.test(max) || exP === null || brP === null) {
    return null;
  }
  const p = Math.max(exP, brP);
  const scaled = (v: string, from: number) => BigInt(v) * 10n ** BigInt(p - from);
  const room = scaled(max, brP) - scaled(exchange, exP);
  return {
    exchange: formatRate(exchange, exP),
    broker: formatRate(broker, brP),
    allowed: formatRate((room > 0n ? room : 0n).toString(), p),
  };
}

const CURRENCY_CODE = /^[A-Z][A-Z0-9_]{0,15}$/;

/**
 * BROKER_CURRENCY_NOT_TRADED's figures (SM-866): the refused currency and
 * the node's currencies (details.node_currencies, a comma list), joined
 * for display. null when either is missing or malformed.
 */
export function currencyNotTradedFigures(d: Record<string, string> | undefined): { currency: string; currencies: string } | null {
  const currency = d?.currency?.trim();
  const traded = (d?.node_currencies ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c !== "");
  if (!currency || !CURRENCY_CODE.test(currency) || traded.length === 0 || !traded.every((c) => CURRENCY_CODE.test(c))) return null;
  return { currency, currencies: traded.join(", ") };
}

const DETAIL_PARAMS: Record<string, (d: Record<string, string> | undefined) => Record<string, string> | null> = {
  BROKER_FEE_CEILING_EXCEEDED: redemptionCeilingFigures,
  BROKER_CURRENCY_NOT_TRADED: currencyNotTradedFigures,
};

/**
 * The copy for one failure: the catalog entry for its code (the rule's
 * copy when it names a rule the catalog knows); otherwise the server's
 * message; otherwise a status fallback. The code and the raw rule are
 * always kept.
 */
export function describeError(err: ErrorLike | null | undefined, locale: Locale): DescribedError {
  const code = err?.code || undefined;
  const rule = err?.details?.[ruleDetailOf(code)] || undefined;
  const serverMessage = isGenericMessage(err?.message) ? undefined : err?.message;
  const catalog = locale === "th" ? TH : EN;
  if (code && hasCatalogEntry(code)) {
    const ruleCopy = rule ? catalogRuleCopy(code, rule, locale) : undefined;
    if (ruleCopy) return { title: ruleCopy.title, hint: ruleCopy.hint, code, rule, serverMessage, fromCatalog: true };
    const entry = catalog[code] ?? EN[code]!;
    const params = DETAIL_PARAMS[code]?.(err?.details);
    const hint = params && entry.detailHint ? interpolate(entry.detailHint, params) : entry.hint;
    return { title: entry.title, hint, code, rule, serverMessage, fromCatalog: true };
  }
  if (serverMessage) return { title: serverMessage, code, rule, fromCatalog: false };
  const byStatus = (locale === "th" ? STATUS_TH : STATUS_EN)[err?.status ?? -1];
  if (byStatus) return { ...byStatus, code, rule, fromCatalog: false };
  return {
    title: locale === "th" ? "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" : "The request failed.",
    code,
    rule,
    fromCatalog: false,
  };
}

/** The details key that names a code's rule: "rule", unless its entry says otherwise (tests). */
export function ruleDetailOf(code: string | undefined): string {
  return (code && hasCatalogEntry(code) && EN[code]!.ruleDetail) || "rule";
}

/** The catalog's copy for a code in a locale (tests and tooltips). */
export function catalogCopy(code: string, locale: Locale): ErrorCopy | undefined {
  if (!hasCatalogEntry(code)) return undefined;
  const entry = (locale === "th" ? TH : EN)[code] ?? EN[code]!;
  return { title: entry.title, hint: entry.hint };
}

/** The catalog's copy for one details.rule of a code, if it has one. */
export function catalogRuleCopy(code: string, rule: string, locale: Locale): ErrorCopy | undefined {
  if (!hasCatalogEntry(code)) return undefined;
  const own = (locale === "th" ? TH : EN)[code]?.rules;
  const en = EN[code]?.rules;
  if (own && Object.prototype.hasOwnProperty.call(own, rule)) return own[rule];
  if (en && Object.prototype.hasOwnProperty.call(en, rule)) return en[rule];
  return undefined;
}

/** The rules a code has copy for (tests). */
export function catalogRules(code: string): string[] {
  return Object.keys(EN[code]?.rules ?? {});
}

/** Codes present in EN but missing in TH (must be empty — see the test). */
export function missingThaiCodes(): string[] {
  return CATALOG_CODES.filter((c) => !Object.prototype.hasOwnProperty.call(TH, c));
}

/** "CODE.RULE" pairs with EN copy but none in TH, and EN detail hints TH lacks (must be empty). */
export function missingThaiRules(): string[] {
  const out: string[] = [];
  for (const code of CATALOG_CODES) {
    for (const rule of catalogRules(code)) {
      if (!Object.prototype.hasOwnProperty.call(TH[code]?.rules ?? {}, rule)) out.push(`${code}.${rule}`);
    }
    if (EN[code]?.detailHint && !TH[code]?.detailHint) out.push(`${code}.detailHint`);
  }
  return out;
}
